import {useQuery} from '@tanstack/react-query';
import baseUrl from '../api/baseUrl';

type ClientConfigData = {
    clientId: string;
    tenantId: string;
    allowedDomains: string[];
    googleTagManagerId: string;
    botSubscriptionKey: string;
    primaryColor: string;
    logoLightModeSrc: string;
    logoDarkModeSrc: string;
    countryName: string;
    isTemplatePublisher: boolean;
    requireAdminApprovalForSharing: boolean;
    showUnlisted: boolean;
    advancedOptionsAdminOnly: boolean;
    useAzureClient: boolean;
    voiceAssistantEnabled: boolean;
    helpdeskEmail: string | null;
    jobsEnabled: boolean;
    multiProviderEnabled: boolean;
    // True only in VisibleInThreads mode — gates the thread ROI widget and feedback UI.
    roiThreadWidgetEnabled: boolean;
    // 'Off' | 'Suggest' | 'Auto'. Off hides every compaction affordance in the thread UI.
    conversationCompactionMode: 'Off' | 'Suggest' | 'Auto';
    // Percent of the context window at which the Suggest banner appears.
    conversationCompactionThresholdPercent: number;
};

const fetchClientConfig = async (): Promise<ClientConfigData> => {
    const response = await fetch(`${baseUrl}/api/client-config`);

    if (!response.ok) {
        throw new Error('Network response was not ok');
    }

    return response.json();
};

const useClientConfig = () => {
    const {data, error, isPending} = useQuery({
        queryKey: ['clientConfig'],
        queryFn: fetchClientConfig,
        staleTime: Infinity,
        retry: 3
    });

    return {
        clientId: data?.clientId ?? '',
        tenantId: data?.tenantId ?? '',
        allowedDomains: data?.allowedDomains ?? ['*'],
        googleTagManagerId: data?.googleTagManagerId ?? '',
        botSubscriptionKey: data?.botSubscriptionKey ?? '',
        primaryColor: data?.primaryColor ?? '',
        logoLightModeSrc:
            data?.logoLightModeSrc ?? '/images/logo-light-mode.svg',
        logoDarkModeSrc: data?.logoDarkModeSrc ?? '/images/logo-dark-mode.svg',
        countryName: data?.countryName ?? '',
        isTemplatePublisher: data?.isTemplatePublisher ?? false,
        requireAdminApprovalForSharing:
            data?.requireAdminApprovalForSharing ?? false,
        showUnlisted: data?.showUnlisted ?? false,
        advancedOptionsAdminOnly: data?.advancedOptionsAdminOnly ?? false,
        useAzureClient: data?.useAzureClient ?? false,
        voiceAssistantEnabled: data?.voiceAssistantEnabled ?? false,
        helpdeskEmail: data?.helpdeskEmail ?? null,
        jobsEnabled: data?.jobsEnabled ?? false,
        multiProviderEnabled: data?.multiProviderEnabled ?? false,
        roiThreadWidgetEnabled: data?.roiThreadWidgetEnabled ?? false,
        conversationCompactionMode:
            data?.conversationCompactionMode ?? 'Suggest',
        conversationCompactionThresholdPercent:
            data?.conversationCompactionThresholdPercent ?? 60,
        error,
        isLoading: isPending
    };
};

export default useClientConfig;
